import { Suspense, useEffect, useMemo, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import type { Material } from 'three';
import {
  DRIVING_FLOOR_RADIUS,
  DRIVING_START_LINE_X,
  DRIVING_START_LINE_Z,
  DRIVING_TRACK_INNER_RADIUS,
  DRIVING_TRACK_WIDTH,
  DRIVING_YARD_RADIUS,
} from '../model/stageGround';
import {
  ASPHALT_TILE_METRES,
  createAsphaltTextures,
  disposeAsphaltTextures,
} from './materials/asphaltSurface';
import { createStartLineTexture } from './materials/trackMarkings';
import { ElevationCourse } from './ElevationCourse';
import { DrivingSurfacePatches } from './DrivingSurfacePatches';
import { TrackBranding } from './TrackBranding';
import { TrackKerb } from './TrackKerb';
import { CourseRouteSurface } from './CourseRouteSurface';
import { COURSE_IDS, type DrivingCourseOption } from '../data/courseOptions';
import type { RuntimeWeather } from '../model/weatherTransition';
import { useWeatherRoadMaterials } from './WeatherAppliers';

/**
 * 주행 모드의 노면.
 *
 * 보기 모드의 단색 원판(StudioFloor)을 대신합니다. 나누는 이유는 두 모드가 노면에
 * 요구하는 것이 다르기 때문입니다. 쇼룸은 차를 돋보이게 하는 무지 배경이 필요하고
 * (프리셋마다 색이 다릅니다), 주행은 **흘러가는 무늬**가 필요합니다 — 단색 바닥에서는
 * 눈이 기준으로 삼을 것이 없어서 달리는지 알 수 없습니다.
 *
 * 표식까지 여기서 함께 그립니다. 경계선과 출발선은 노면에 칠한 것이라 노면과 같은
 * 컴포넌트에 있어야 높이 관계(누가 누구 위에 있는지)를 한 곳에서 볼 수 있습니다.
 *
 * 안쪽에서 바깥쪽으로 세 구역입니다.
 *   인필드      0 ~ 1175m     자유 주행 공간. 중앙 +X 직선에는 경사 코스가 있습니다.
 *   트랙        1176 ~ 1200m  테두리를 따라 도는 고속 차선과 흰 선·적백 커브.
 *   이탈 구간   1201 ~ 1252m  어두운 노면. 반경 1236m를 넘으면 출발선으로 돌아옵니다.
 *
 * 중앙 경사 코스는 120~300m 오르막, 300~380m 평탄부, 380~560m 내리막입니다.
 * 렌더와 차량 자세가 model/drivingTerrain의 같은 높이 함수를 읽으므로 차가 경사면을
 * 뚫거나 떠 있지 않습니다.
 *
 * 트랙을 벗어나도(인필드로 들어가도) 벌칙은 없습니다. 물리적으로 막는 것은 이탈 구간
 * 끝뿐입니다. 차선을 강제하면 쇼룸에서 차를 구경하려고 들어온 사람에게 규칙을 지키라고
 * 요구하는 셈이 되고, 이 모드의 목적은 경기가 아니라 움직이는 차를 보는 것입니다.
 * 트랙은 "여기를 달리면 재미있다"는 안내이지 벽이 아닙니다.
 */

/** 바닥을 y=0보다 살짝 내려 차량 하단 메시와 z-fighting이 나지 않게 합니다(StudioFloor와 같은 값). */
const GROUND_Y = -0.002;
/** 칠한 표식은 노면 위. 접지면(y=0)보다는 아래에 둡니다. */
const PAINT_Y = -0.0014;
const START_LINE_Y = -0.0012;

/** 경계선 두께 (m). 실제 트랙의 흰 선(10~15cm)보다 굵게 잡습니다 — 코스 반대편에서도 보여야 합니다. */
const BOUNDARY_LINE_WIDTH = 0.6;
/**
 * 커브 폭 (m). 트랙 노면 **밖**에 붙습니다.
 *
 * 트랙 경계를 알려주는 가장 강한 표식입니다. 차량은 이 선을 넘을 수 있고(바깥으로
 * 넘어서 이탈 구간을 지나면 리스폰하고, 안쪽으로 넘으면 그냥 인필드입니다) 그래서
 * 담장이 아니라 노면 끝 표시로 읽혀야 합니다. 실제 트랙 커브와 같은 1m로 잡습니다 —
 * 코스 반대편(160m)에서도 적백 무늬가 구분됩니다.
 */
const KERB_WIDTH = 1;
const KERB_Y = -0.0013;

/**
 * 테두리를 따라 도는 트랙 치수는 초기 차량 위치와 같아야 하므로 stageGround에서
 * 공유합니다. 24m 폭의 중앙 반경은 1,188m이고, 322km/h에서도 필요한 횡가속도가
 * 약 6.7m/s²라 그립 한계 9m/s² 안에 들어옵니다.
 */

/**
 * 커브 밖 이탈 구간의 어두운 덧칠.
 *
 * 없으면 커브 안과 밖이 똑같은 아스팔트여서, 리스폰이 아무 근거 없이 일어나는 것처럼
 * 보입니다(이유를 모르는 순간이동은 버그로 읽힙니다). 색을 눌러 두면 커브를 넘는 순간
 * 화면이 어두워져서 "여기는 코스가 아니다"가 먼저 전달되고, 리스폰은 그 결과가 됩니다.
 *
 * 노면을 갈아 끼우지 않고 덧칠로 얹는 이유는 아스팔트 알갱이를 살려야 하기 때문입니다.
 * 이탈 구간에서도 속도감은 남아 있어야 돌아올 방향을 잡을 수 있습니다.
 */
const RUNOFF_Y = -0.0016;

/**
 * 원형 표식의 분할 수. 반경에 비례시킵니다.
 *
 * 고정값을 쓰면 반경을 키울 때 조용히 다각형이 됩니다 — 80m 링을 128분할하면 변 하나가
 * 3.9m라서, 커브 옆을 지날 때 원이 아니라 꺾인 선으로 보입니다. 반경에 비례시키면 변
 * 길이가 어느 반경에서나 1.6m로 유지됩니다.
 */
function ringSegments(radius: number): number {
  return Math.round(radius * 4);
}

/** 출발·결승선의 주행 방향 두께 (m). 가로 길이는 트랙 폭을 그대로 씁니다. */
const START_LINE_DEPTH = 0.9;
/** 체커 한 칸의 실제 크기 (m). 이 값으로 repeat을 만듭니다. */
const CHECKER_CELL_METRES = 0.45;

export interface DrivingGroundProps {
  weather: RuntimeWeather;
  course?: DrivingCourseOption;
  /** 그래픽 품질이 정하는 비등방 필터링 상한입니다. */
  anisotropy?: number;
}

export function DrivingGround({
  weather,
  course,
  anisotropy: anisotropyLimit = 8,
}: DrivingGroundProps) {
  /*
   * 날씨 값은 렌더에서 읽지 않습니다. 전환 중에 재렌더가 일어나면 노면 전체가 재조정되고
   * 절차적 환경맵까지 다시 구워집니다. 재질 ref를 만들어 프레임 루프에서 값만 씁니다.
   */
  const asphaltMaterialRef = useRef<Material | Material[] | null>(null);
  const groundMaterialRef = useRef<Material | Material[] | null>(null);
  useWeatherRoadMaterials(weather, {
    asphalt: asphaltMaterialRef,
    ground: groundMaterialRef,
  });
  /*
   * 최대 비등방 필터링. 노면은 시선과 거의 나란해서, 이걸 켜지 않으면 조금만 멀어져도
   * 알갱이가 회색으로 뭉개집니다 — 속도감을 만드는 것이 그 알갱이입니다.
   */
  const maxAnisotropy = useThree((state) => state.gl.capabilities.getMaxAnisotropy());
  const anisotropy = Math.min(anisotropyLimit, maxAnisotropy);

  const asphalt = useMemo(() => createAsphaltTextures(), []);
  const startLine = useMemo(() => createStartLineTexture(), []);

  useEffect(() => () => disposeAsphaltTextures(asphalt), [asphalt]);
  useEffect(() => () => startLine?.dispose(), [startLine]);

  /*
   * 반복 횟수는 노면 지름을 타일 크기로 나눈 값입니다. CircleGeometry의 UV는 지름을
   * 감싸는 정사각형에 0~1로 펼쳐지므로, 지름을 기준으로 계산해야 실제 크기와 맞습니다.
   */
  useMemo(() => {
    if (asphalt === null) {
      return;
    }
    const repeat = (DRIVING_FLOOR_RADIUS * 2) / ASPHALT_TILE_METRES;
    [asphalt.color, asphalt.normal, asphalt.roughness].forEach((texture) => {
      texture.repeat.set(repeat, repeat);
      texture.anisotropy = anisotropy;
      texture.needsUpdate = true;
    });
  }, [asphalt, anisotropy]);

  useMemo(() => {
    if (startLine === null) {
      return;
    }
    /*
     * 텍스처는 2×2 칸이므로 실제 칸 수의 절반을 repeat으로 줍니다. u는 판의 첫 번째
     * 변(트랙을 가로지르는 방향), v는 두 번째 변(주행 방향)입니다.
     */
    startLine.repeat.set(
      DRIVING_TRACK_WIDTH / CHECKER_CELL_METRES / 2,
      START_LINE_DEPTH / CHECKER_CELL_METRES / 2
    );
    startLine.anisotropy = anisotropy;
    startLine.needsUpdate = true;
  }, [startLine, anisotropy]);

  return (
    <>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, GROUND_Y, 0]} receiveShadow={false}>
        {/* 분할 수를 반경에 비례시킵니다. 고정 64분할이면 큰 원판에서 테두리가 다각형으로 보입니다. */}
        <circleGeometry args={[DRIVING_FLOOR_RADIUS, ringSegments(DRIVING_FLOOR_RADIUS)]} />
        {/* 색은 useWeatherRoadMaterials가 매 프레임 씁니다(눈·젖은 잔디). */}
        <meshStandardMaterial ref={groundMaterialRef} color="#527346" roughness={0.96} metalness={0} />
      </mesh>

      {/* 외곽 고속 링만 아스팔트로 덮습니다. 나머지 인필드는 잔디 노면입니다. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, GROUND_Y + 0.0008, 0]}>
        <ringGeometry
          args={[
            DRIVING_TRACK_INNER_RADIUS,
            DRIVING_YARD_RADIUS,
            ringSegments(DRIVING_YARD_RADIUS),
          ]}
        />
        {asphalt === null ? (
          <meshStandardMaterial ref={asphaltMaterialRef} color="#3a3d42" roughness={0.72} metalness={0.04} />
        ) : (
          <meshStandardMaterial
            ref={asphaltMaterialRef}
            map={asphalt.color}
            normalMap={asphalt.normal}
            roughnessMap={asphalt.roughness}
            roughness={0.72}
            metalness={0.04}
            envMapIntensity={0.35}
          />
        )}
      </mesh>

      {/* 중앙 +X 직선의 오르막·내리막. 평면과 같은 텍스처를 공유해 이음새를 숨깁니다. */}
      <ElevationCourse asphalt={asphalt} weather={weather} />
      {course !== undefined
      && course.id !== COURSE_IDS.SPEED_RING
      && course.id !== COURSE_IDS.ELEVATION_RUN ? (
        <CourseRouteSurface course={course} asphalt={asphalt} weather={weather} />
      ) : null}
      <DrivingSurfacePatches weather={weather} />

      {/* 로고 로딩이 늦어도 노면 전체 Suspense를 막지 않습니다. */}
      <Suspense fallback={null}>
        <TrackBranding />
      </Suspense>

      {/*
        커브 밖 이탈 구간. 커브 바깥 끝에서 노면 끝까지 덮습니다.

        리스폰 반경까지가 아니라 노면 끝까지 칠하는 이유: 리스폰은 차량 **원점**이
        기준이라 차 앞부분이 그 선을 지나쳐 있고, 카메라도 노면 끝 근처까지 나갑니다.
        중간에 색이 돌아오면 "여기서부터 다시 코스"로 읽혀서 거짓말이 됩니다.
      */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, RUNOFF_Y, 0]}>
        <ringGeometry
          args={[DRIVING_YARD_RADIUS + KERB_WIDTH, DRIVING_FLOOR_RADIUS, ringSegments(DRIVING_FLOOR_RADIUS)]}
        />
        {/*
          depthWrite를 끕니다. 노면 위 0.4mm에 얹힌 반투명 판이라, 깊이를 쓰면 같은
          거리의 다른 표식과 순서 싸움이 붙어 기기마다 다르게 보입니다.
        */}
        <meshStandardMaterial color="#6d6458" roughness={0.98} metalness={0} />
      </mesh>

      {/*
        트랙 바깥 경계선. 차량은 이 선을 넘을 수 있습니다 — 넘어서 이탈 구간
        (DRIVING_RESPAWN_RADIUS)까지 가면 트랙 출발선에서 다시 출발합니다.
      */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, PAINT_Y, 0]}>
        <ringGeometry
          args={[
            DRIVING_YARD_RADIUS - BOUNDARY_LINE_WIDTH,
            DRIVING_YARD_RADIUS,
            ringSegments(DRIVING_YARD_RADIUS),
          ]}
        />
        {/*
          칠한 선이라 조명을 받습니다(Basic이 아니라 Standard). 그래야 배경 프리셋이
          바뀔 때 노면과 함께 밝아지고 어두워져서 같은 면에 칠한 것처럼 보입니다.
        */}
        <meshStandardMaterial color="#e8e8ea" roughness={0.6} metalness={0} />
      </mesh>

      {/* 바깥 경계선 밖의 적백 커브. */}
      <TrackKerb innerRadius={DRIVING_YARD_RADIUS} width={KERB_WIDTH} y={KERB_Y} anisotropy={anisotropy} />

      {/*
        트랙 안쪽 경계선. 이 선이 생기면서 테두리를 따라 도는 차선이 만들어집니다.

        바깥과 대칭으로 그립니다 — 선은 트랙 노면 안쪽 끝에, 커브는 그 선 밖(중앙 쪽)에.
        차선 양쪽이 같은 언어로 표시되어야 "여기가 트랙 폭"이 한 번에 읽힙니다.
      */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, PAINT_Y, 0]}>
        <ringGeometry
          args={[
            DRIVING_TRACK_INNER_RADIUS,
            DRIVING_TRACK_INNER_RADIUS + BOUNDARY_LINE_WIDTH,
            ringSegments(DRIVING_TRACK_INNER_RADIUS),
          ]}
        />
        <meshStandardMaterial color="#e8e8ea" roughness={0.6} metalness={0} />
      </mesh>

      <TrackKerb
        innerRadius={DRIVING_TRACK_INNER_RADIUS - KERB_WIDTH}
        width={KERB_WIDTH}
        y={KERB_Y}
        anisotropy={anisotropy}
      />

      {/*
        출발·결승선. 트랙의 +X 쪽에서 폭 전체를 가로지릅니다.

        차량은 이 선보다 4m 앞에서 -Z 접선 방향으로 시작하므로 주행 모드에 들어오면
        곧바로 체커 라인을 통과합니다. 판의 첫 번째 변(월드 X)은 트랙 폭, 두 번째 변
        (월드 Z)은 선의 두께입니다.
      */}
      {startLine === null ? null : (
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[DRIVING_START_LINE_X, START_LINE_Y, DRIVING_START_LINE_Z]}
        >
          <planeGeometry args={[DRIVING_TRACK_WIDTH, START_LINE_DEPTH]} />
          <meshStandardMaterial map={startLine} roughness={0.6} metalness={0} />
        </mesh>
      )}
    </>
  );
}
